import { useState, useEffect } from "react";
import axios from 'axios';
import { useCookies } from 'react-cookie';
import { useToast } from "../../lib/useToast"
import { Toaster } from "../../components/Toaster"
import { ArrowUpRightIcon } from "@heroicons/react/24/outline";
import { ptBR } from "date-fns/locale";
import { 
  Button,
  Dropdown,
  DropdownItem,
  Grid,
  Col,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  Title,
  TextInput,
  Text,
  DateRangePicker,
  DateRangePickerValue,
  Accordion,
  AccordionBody,
  AccordionHeader,
  Card
} from "@tremor/react";
import { Statement } from "../../interfaces/FinanceInterface";
import { User } from "../../interfaces/AccessInterfaces";

export default function BillingCustumersComponent() { 
  const [cookies] = useCookies(['mazelog-user']);
  const token = cookies['mazelog-user']?.token_data?.access_token;
  const orgId = cookies["mazelog-user"]?.organization_data?.id_org;
  const userProfile = cookies['mazelog-user']?.user_data?.user_profile;
  const isTransporter = userProfile === "TRANSPORTER" || userProfile === "TRANSPORTER_COLLECTER";

  const [requested, setRequested] = useState<boolean>(false);
  const [statements, setStatements] = useState<Statement[]>([])
  const [selectedUser, setSelectedUser] = useState<string>("");
  const [users, setUsers] = useState<User[]>([]);
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [editingStatement, setEditingStatement] = useState<Statement | null>(null);
  
   const { toast } = useToast()

  const now = new Date();
  const maxDate = new Date();

  const [selectedDateRange, setSelectedDateRange] = useState<DateRangePickerValue>([
    new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7),
    now
  ]);

  const toggleRow = (id: string) => {
    setExpandedRows(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const getUsers = () => {
    const baseURL = process.env.REACT_APP_MAZELOG_AUTH_BASE_URL;
    const oauth = "Bearer " + token;

    let url = `${baseURL}/users`;
    if (isTransporter) {
      url = `${baseURL}/users/company/${orgId}`;
    }

    axios.get(url,
      {headers: {accept: 'application/json', "Authorization": oauth}}
    )
    .then((res)=>{
      console.log(res);
      setUsers(res.data.data);
    })
    .catch((err)=>{
      console.log(err);
    })
  }

  const getStatements = () => {
    const baseURL = process.env.REACT_APP_MAZELOG_SYSTEM_BASE_URL;
    const oauth = "Bearer " + token;
    axios.get(`${baseURL}/prices/statement/${orgId}`,
      {headers: {accept: 'application/json', "Authorization": oauth}}
    )
    .then((res)=>{
      console.log(res);
      if (res.data != null) setStatements(res.data.results)
    })
    .catch((err)=>{
      console.log(err);
    })
  }

  const handleUpdateStatement = async (statement: Statement) => {

    const baseURL = process.env.REACT_APP_MAZELOG_SYSTEM_BASE_URL;
    const oauth = "Bearer " + token;
    
    const updateStatement = {...statement}

    await axios.put(`${baseURL}/prices/statement/`,
      {...updateStatement},
      {headers: {accept: 'application/json', "Authorization": oauth}},
    )
    
    getStatements()
  }

  const createStatements = () => {
    if (!selectedUser) {
      toast({
        title: "Warning",
        description: "Selecione um usuario",
        variant: "warning",
        duration: 3000,
      })
      return;
    }

    const startDate = selectedDateRange[0]?.toISOString().split('T')[0];
    const endDate = selectedDateRange[1]?.toISOString().split('T')[0];
    
    const baseURL = process.env.REACT_APP_MAZELOG_SYSTEM_BASE_URL;
    const oauth = "Bearer " + token;
    axios.get(`${baseURL}/prices/statement/?user_id=${selectedUser}&date_start=${startDate}&date_end=${endDate}`,
      {headers: {accept: 'application/json', "Authorization": oauth}}
    )
    .then( async (res)=>{
      console.log(res);
      await getStatements();
    })
    .catch((err)=>{
      console.log(err);
      toast({
        title: "Erro",
        description: err.response.data.detail,
        variant: "error",
        duration: 3000,
      })
    })
  }

  const openStatement = (id: string) => {
    toggleRow(id);
    const selected = statements.find((s) => s._id === id);
    if (selected) {
      setEditingStatement({ ...selected });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
  
    const cleanedValue = value.replace(/[^0-9]/g, "");
    const numericValue = cleanedValue === "" ? 0 : Number(cleanedValue);
  
    setEditingStatement((prev) => {
      if (!prev) return prev;
  
      const updated = {
        ...prev,
        [name]: numericValue,
      };
  
      if (name === "collect_quantity") {
        updated.total_collect_value = numericValue * updated.collect_value;
      }
      if (name === "meli_ship_quantity") {
        updated.total_meli_ship_value = numericValue * updated.meli_ship_value;
      }
      if (name === "shopee_ship_quantity") {
        updated.total_shopee_ship_value = numericValue * updated.shopee_ship_value;
      }
      if (name === "ecom_ship_quantity") {
        updated.total_ecom_ship_value = numericValue * updated.ecom_ship_value;
      }
  
      return updated;
    });
  };
  
  useEffect(()=>{
    if (!requested){
      getUsers();
      getStatements();
      setRequested(true);
    }
  },[requested])

  return (
    <div>
      <div className="flex gap-4 mb-4">
        <div>
          <label className="LabelPrimary">Data</label>
          <DateRangePicker
            className="max-w-md"
            value={selectedDateRange}
            onValueChange={setSelectedDateRange}
            locale={ptBR}
            dropdownPlaceholder="Selecionar"
            enableYearPagination={true}
            maxDate={maxDate}
          />

        </div>
        <div className="w-1/4">
          <label className="LabelPrimary">Motorista</label>
          <Dropdown
            className=""
            value={selectedUser}
            onValueChange={(value) => setSelectedUser(value)}
            placeholder="Selecione um usuário"
          >
            {users.filter(x =>  x.user_profile === "TRANSPORTER" || x.user_profile === "TRANSPORTER_COLLECTER").map((item) => (
              <DropdownItem 
                key={item.id_user} 
                value={item.id_user?.toString() ?? ""} 
                text={item.email} 
              />
            ))}
          </Dropdown>
        </div>
        <Button className="FilterButton mt-auto" onClick={() => createStatements()}>
          Solicitar Fatura
        </Button>
      </div>
      
      <Card>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeaderCell className="text-left">Nome Completo</TableHeaderCell>
              <TableHeaderCell className="text-left">ID Usuario</TableHeaderCell>
              <TableHeaderCell className="text-left">Periodo</TableHeaderCell>
              <TableHeaderCell className="text-left">Valor R$</TableHeaderCell>
              <TableHeaderCell className="text-center">Status</TableHeaderCell>
              <TableHeaderCell className="text-center">Abrir</TableHeaderCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {statements
            .filter((statement) => {
              const user = users.find((u) => u.id_user === statement.id_client);
              return user?.user_profile === "TRANSPORTER" || user?.user_profile === "TRANSPORTER_COLLECTER";
            })
            .map((statement) => {
              const rowUser = users.find(x=> x.id_user === statement.id_client)
              return (
                <>
                  <TableRow key={statement._id}>
                    <TableCell>{rowUser?.full_name ?? rowUser?.username}</TableCell>
                    <TableCell>{statement.id_client}</TableCell>
                    <TableCell>{statement.date_start} a { statement.date_end}</TableCell>
                    <TableCell>R$ {(statement.total_collect_value + statement.total_meli_ship_value + statement.total_ecom_ship_value + statement.total_shopee_ship_value + statement.discount ).toFixed(2) }</TableCell>
                    <TableCell className="text-center">{statement.is_paid ? "Pago": "Pendente"}</TableCell>
                    <TableCell className="text-center">
                      <Button 
                        size="xs" 
                        variant="light" 
                        icon={ArrowUpRightIcon} 
                        onClick={() => openStatement(statement._id ?? "")}
                      />
                    </TableCell>
                  </TableRow>
                  
                  {expandedRows[statement._id ?? ""] && (
                    <TableRow className="p-2">
                      <TableCell colSpan={6} className="p-0">
                        <Accordion>
                          <AccordionBody>
                            <div className="p-4">
                            <Grid numCols={2} className="gap-4">
                            <Col>
                              <Text>Quantidade Entregas - Mercado Livre</Text>
                              <input 
                                className="InputPrimary" 
                                type="number"
                                name="meli_ship_quantity"
                                value={editingStatement?.meli_ship_quantity ?? 0}
                                onChange={handleInputChange}
                              />
                            </Col>

                            <Col>
                              <Text>Valor Total Entregas - Mercado Livre</Text>
                              <input  className="InputPrimary" 
                                type="number"
                                name="total_meli_ship_value"
                                value={editingStatement?.total_meli_ship_value ?? 0}
                                onChange={handleInputChange}
                                disabled
                              />
                            </Col>

                            <Col>
                              <Text>Quantidade Entregas - Shopee</Text>
                              <input  className="InputPrimary" 
                                type="number"
                                name="shopee_ship_quantity"
                                value={editingStatement?.shopee_ship_quantity ?? 0}
                                onChange={handleInputChange}
                              />
                            </Col>

                            <Col>
                              <Text>Valor Total Entregas - Shopee</Text>
                              <input  className="InputPrimary" 
                                type="number"
                                name="total_shopee_ship_value"
                                value={editingStatement?.total_shopee_ship_value ?? 0}
                                onChange={handleInputChange}
                                disabled
                              />
                            </Col>

                            <Col>
                              <Text>Quantidade Entregas - E-commerce</Text>
                              <input  className="InputPrimary" 
                                type="number"
                                name="ecom_ship_quantity"
                                value={editingStatement?.ecom_ship_quantity ?? 0}
                                onChange={handleInputChange}
                              />
                            </Col>

                            <Col>
                              <Text>Valor Total Entregas - E-commerce</Text>
                              <input  className="InputPrimary" 
                                type="number"
                                name="total_ecom_ship_value"
                                value={editingStatement?.total_ecom_ship_value ?? 0}
                                onChange={handleInputChange}
                                disabled
                              />
                            </Col>

                            <Col>
                              <Text>Quantidade Coleta</Text>
                              <input  className="InputPrimary" 
                                type="number"
                                name="collect_quantity"
                                value={editingStatement?.collect_quantity ?? 0}
                                onChange={handleInputChange}
                              />
                            </Col>

                            <Col>
                              <Text>Valor Coleta</Text>
                              <input  className="InputPrimary" 
                                type="number"
                                name="total_collect_value"
                                value={editingStatement?.total_collect_value ?? 0}
                                onChange={handleInputChange}
                                disabled
                              />
                            </Col>

                            <Col>
                              <Text>Adicional (+) / Desconto (-)</Text>
                              <input  className="InputPrimary" 
                                type="number"
                                name="discount"
                                defaultValue={editingStatement?.discount?.toString() ?? ''}
                                onChange={(e) => {
                                  setEditingStatement(prev => {
                                    if (!prev) return prev;
                                    return {
                                      ...prev,
                                      discount: parseFloat(e.target.value) || 0,
                                    };
                                  });
                                }}
                                step="0.01"
                              />
                            </Col>

                              <Col>
                                <Text>Status</Text>
                                <Dropdown
                                  className="w-full"
                                  value={editingStatement?.is_paid !== undefined ? String(editingStatement.is_paid) : undefined}
                                  onValueChange={(value) => {
                                    setEditingStatement((prev) => {
                                      if (!prev) return prev;
                                      return {
                                        ...prev,
                                        is_paid: value === "true", // converte string -> boolean
                                      };
                                    });
                                  }}
                                  placeholder="Status"
                                >
                                  <DropdownItem value="true" text="Pago" />
                                  <DropdownItem value="false" text="Pendente" />
                                </Dropdown>
                              </Col>
                              
                              <Col numColSpan={2} className="">
                                <Text>Anotações</Text>
                                <TextInput 
                                    className="mt-1"
                                    name="note"
                                    value={editingStatement?.note}
                                    placeholder="Anotações"
                                    onChange={(e) =>
                                      setEditingStatement((prev) => prev ? { ...prev, note: e.target.value } : prev)
                                    }
                                  />
                              </Col>    

                              <Col className="flex items-end">
                                <Button 
                                  className="FilterButton w-full"
                                  onClick={() => editingStatement && handleUpdateStatement(editingStatement)}
                                >
                                  Atualizar Fatura
                                </Button>
                              </Col>
                            </Grid>
                            </div>
                          </AccordionBody>
                        </Accordion>
                      </TableCell>
                    </TableRow>
                  )}
                </>
              )
            } 
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  )
}